import { useState } from 'react';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { ModalHeader } from './ModalHeader';
import { SearchResults } from './SearchResults';
import { SearchSection } from './SearchSection';
import {
  ModalContentStyle,
  ModalOverlayStyle,
  ModalLoaderStyle,
  ModalSpinnerStyle,
  InvalidContractsErrorStyle,
  InvalidContractsErrorIconStyle,
  InvalidContractsErrorTextStyle,
  InvalidContractsErrorCloseStyle,
} from '../ContractIdSelectionModal.style';
import type { ContractIdData, ContractIdSelectionPayload } from '../types/ContractIdModal.types';
import { tinLimit } from '@src/apps/constants/constant';

export const TAX_ID_LIMIT = 1000;
// Same threshold used by the normal vs bulk TIN flow: <= tinLimit stays on the normal contract flow, > tinLimit goes to the bulk contract flow
export const CONTRACT_BULK_FLOW_THRESHOLD = tinLimit;
// Same page-size options used by the bulk TIN flow's footer pagination
const PAGINATION_CONFIG = [
  { value: 20, label: 20 },
  { value: 40, label: 40 },
  { value: 80, label: 80 },
];

interface ContractIdSelectionModalProps {
  isOpen: boolean;
  contractIds?: ContractIdData[];
  initialSelectedIds?: string[];
  invalidContractIds?: string[];
  isLoading?: boolean;
  error?: string | null;
  onClose: () => void;
  onApply: (selection: ContractIdSelectionPayload) => void;
}

type PaginationEvent = {
  startIndex: number;
  endIndex: number;
};

function ContractIdSelectionModalContent({
  isOpen,
  contractIds = [],
  initialSelectedIds,
  invalidContractIds = [],
  isLoading = false,
  error = null,
  onClose,
  onApply,
}: ContractIdSelectionModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    if (initialSelectedIds !== undefined) {
      const availableIds = new Set(contractIds.map((contract) => contract.contractId));
      return new Set(initialSelectedIds.filter((contractId) => availableIds.has(contractId)));
    }

    const totalTaxIdCount = contractIds.reduce((total, contract) => total + contract.taxIdCount, 0);
    return totalTaxIdCount <= TAX_ID_LIMIT
      ? new Set(contractIds.map((contract) => contract.contractId))
      : new Set();
  });
  const [searchResults, setSearchResults] = useState<ContractIdData[]>(contractIds);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(PAGINATION_CONFIG[0].value);
  const [isInvalidContractsBannerDismissed, setIsInvalidContractsBannerDismissed] = useState(false);
  const [isOverLimitBannerDismissed, setIsOverLimitBannerDismissed] = useState(false);
  const [isSearchLimitBannerDismissed, setIsSearchLimitBannerDismissed] = useState(false);
  const [isNoValidContractsBannerDismissed, setIsNoValidContractsBannerDismissed] = useState(false);

  if (!isOpen) return null;

  const selectedContracts = contractIds.filter((contract) => selectedIds.has(contract.contractId));
  const selectedTaxIdCount = selectedContracts.reduce(
    (total, contract) => total + contract.taxIdCount,
    0
  );
  const remainingTaxIds = TAX_ID_LIMIT - selectedTaxIdCount;
  const searchTaxIdCount = searchResults.reduce(
    (total, contract) => total + contract.taxIdCount,
    0
  );
  const visibleSelectedCount = searchResults.filter((contract) =>
    selectedIds.has(contract.contractId)
  ).length;
  const overLimitContracts = searchResults.filter((contract) => contract.taxIdCount > TAX_ID_LIMIT);

  const toggleSelection = (contractId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) {
        next.delete(contractId);
      } else {
        next.add(contractId);
      }
      return next;
    });
  };

  const handleSearch = (query: string) => {
    setSearchResults(
      contractIds.filter((contract) =>
        contract.contractId.toLowerCase().includes(query.toLowerCase())
      )
    );
    setStartIndex(0);
    setEndIndex(PAGINATION_CONFIG[0].value);
  };

  const pagedResults = searchResults.slice(startIndex, Math.min(endIndex, searchResults.length));

  const deselectVisibleResults = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pagedResults.forEach((contract) => next.delete(contract.contractId));
      return next;
    });
  };

  // mirrors the bulk TIN flow's onClickPagination handler
  const onPaginationChange = (event: PaginationEvent) => {
    setStartIndex(event.startIndex < 0 ? 0 : event.startIndex);
    setEndIndex(event.endIndex === 0 ? event.endIndex - event.startIndex : event.endIndex);
  };

  const handleApply = () => {
    onApply({
      selectedContractIds: Array.from(selectedIds),
      selectedContracts,
      selectedTaxIdCount,
      isBulkFlow: selectedTaxIdCount > CONTRACT_BULK_FLOW_THRESHOLD,
    });
  };

  return (
    <ModalOverlayStyle role="dialog" aria-modal="true" aria-labelledby="modalTitle">
      <ModalContentStyle>
        <ModalHeader onClose={onClose} isEditMode={initialSelectedIds !== undefined} />
        {isLoading && (
          <ModalLoaderStyle data-testid="spinning-loader">
            <ModalSpinnerStyle />
          </ModalLoaderStyle>
        )}
        {error && <InvalidContractsErrorStyle role="alert">{error}</InvalidContractsErrorStyle>}
        {!isLoading && !error && contractIds.length > 0 && (
          <>
            {invalidContractIds.length > 0 && !isInvalidContractsBannerDismissed && (
              <InvalidContractsErrorStyle role="alert">
                <InvalidContractsErrorIconStyle>
                  <IconSymbol icon="error" size={20} color="#C40000" />
                </InvalidContractsErrorIconStyle>
                <InvalidContractsErrorTextStyle>
                  The following Contract IDs could not be processed: {invalidContractIds.join(', ')}
                </InvalidContractsErrorTextStyle>
                <InvalidContractsErrorCloseStyle
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setIsInvalidContractsBannerDismissed(true)}
                >
                  <IconSymbol icon="close" color="#000000" />
                </InvalidContractsErrorCloseStyle>
              </InvalidContractsErrorStyle>
            )}
            {overLimitContracts.length > 0 && !isOverLimitBannerDismissed && (
              <InvalidContractsErrorStyle role="alert">
                <InvalidContractsErrorIconStyle>
                  <IconSymbol icon="error" size={20} color="#C40000" />
                </InvalidContractsErrorIconStyle>
                <InvalidContractsErrorTextStyle>
                  The following Contract ID is associated with more than{' '}
                  {TAX_ID_LIMIT.toLocaleString()} Tax IDs:{' '}
                  {overLimitContracts.map((contract) => contract.contractId).join(', ')}. Please
                  search this Contract ID individually from the home screen.
                </InvalidContractsErrorTextStyle>
                <InvalidContractsErrorCloseStyle
                  type="button"
                  aria-label="Dismiss over-limit contract warning"
                  onClick={() => setIsOverLimitBannerDismissed(true)}
                >
                  <IconSymbol icon="close" color="#000000" />
                </InvalidContractsErrorCloseStyle>
              </InvalidContractsErrorStyle>
            )}
            {searchTaxIdCount > TAX_ID_LIMIT &&
              selectedIds.size === 0 &&
              !isSearchLimitBannerDismissed && (
                <InvalidContractsErrorStyle role="alert">
                  <InvalidContractsErrorIconStyle>
                    <IconSymbol icon="error" size={20} color="#C40000" />
                  </InvalidContractsErrorIconStyle>
                  <InvalidContractsErrorTextStyle>
                    Your search exceeds the maximum limit of {TAX_ID_LIMIT.toLocaleString()} Tax
                    IDs. No Contract IDs were selected. Please refine your Contract ID selection to
                    proceed.
                  </InvalidContractsErrorTextStyle>
                  <InvalidContractsErrorCloseStyle
                    type="button"
                    aria-label="Dismiss search limit warning"
                    onClick={() => setIsSearchLimitBannerDismissed(true)}
                  >
                    <IconSymbol icon="close" color="#000000" />
                  </InvalidContractsErrorCloseStyle>
                </InvalidContractsErrorStyle>
              )}
          </>
        )}
        {!isLoading && !error && contractIds.length === 0 && !isNoValidContractsBannerDismissed && (
          <InvalidContractsErrorStyle role="alert">
            <InvalidContractsErrorIconStyle>
              <IconSymbol icon="error" size={20} color="#C40000" />
            </InvalidContractsErrorIconStyle>
            <InvalidContractsErrorTextStyle>
              None of the Contract IDs could be processed. Verify the Contract IDs and try again.
            </InvalidContractsErrorTextStyle>
            <InvalidContractsErrorCloseStyle
              type="button"
              aria-label="Dismiss no valid contracts warning"
              onClick={() => setIsNoValidContractsBannerDismissed(true)}
            >
              <IconSymbol icon="close" color="#000000" />
            </InvalidContractsErrorCloseStyle>
          </InvalidContractsErrorStyle>
        )}
        {!isLoading && !error && contractIds.length > 0 && (
          <>
            <SearchSection
              onSearch={handleSearch}
              selectedCount={visibleSelectedCount}
              totalContractCount={searchResults.length}
              selectedTaxIdCount={selectedTaxIdCount}
              remainingTaxIds={remainingTaxIds}
              taxIdLimit={TAX_ID_LIMIT}
            />
            <SearchResults
              results={pagedResults}
              selectedIds={selectedIds}
              remainingTaxIds={remainingTaxIds}
              taxIdLimit={TAX_ID_LIMIT}
              rangeStart={searchResults.length === 0 ? 0 : startIndex + 1}
              rangeEnd={Math.min(endIndex, searchResults.length)}
              totalCount={searchResults.length}
              onToggle={toggleSelection}
              onDeselectAll={deselectVisibleResults}
              hasSelections={selectedIds.size > 0}
              selectedCount={selectedIds.size}
              onClose={onClose}
              onApply={handleApply}
              tableData={searchResults}
              paginationConfig={PAGINATION_CONFIG}
              onPaginationChange={onPaginationChange}
            />
          </>
        )}
      </ModalContentStyle>
    </ModalOverlayStyle>
  );
}

export function ContractIdSelectionModal(props: ContractIdSelectionModalProps) {
  const contractIdsKey =
    props.contractIds
      ?.map((contract) => `${contract.contractId}:${contract.taxIdCount}`)
      .join('|') ?? '';
  const initialSelectedIdsKey = props.initialSelectedIds?.join('|') ?? '';

  return (
    <ContractIdSelectionModalContent
      key={`${props.isOpen}-${contractIdsKey}-${initialSelectedIdsKey}`}
      {...props}
    />
  );
}
